import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {
    validateCharacters,
    validateDecimalPlaces,
    validateMaxLength,
    validateStreetFrontage
} from 'lib/Validations';
import RangeSelectArea from 'components/FilterFields/RangeSelectArea';
import {
    ONE_TO_SIX_OPTIONS_MAX,
    ONE_TO_SIX_OPTIONS_MIN,
    STREET_FRONTAGE_OPTIONS_MIN,
    STREET_FRONTAGE_OPTIONS_MAX,
} from 'constants/filterOptions';
import RangeAutocomplete from 'components/FilterFields/RangeAutocomplete';
import SearchFilterUtil from 'lib/SearchFilterUtil';
import YearBuiltFilter from 'components/FilterFields/YearBuiltFilter';
import CheckboxFilter from 'components/commons/CheckboxFilter';

function StructureFilter(props) {
    const validateInput = (input) => {
        return (
            validateCharacters(input) ||
            validateMaxLength(input) ||
            validateDecimalPlaces(input)
        );
    };
    const validateDistanceInput = (input) => {
        return (
            validateStreetFrontage(input) ||
            validateMaxLength(input) ||
            validateDecimalPlaces(input)
        );
    };
    return (
        <Box
            sx={{
                mb: 2,
                width: '100%',
            }}
        >
            <Typography sx={{ mb: 2.5, display: 'block' }} variant="text4" gutterBottom>Structure</Typography>
            <Grid container spacing={3}>
                <Grid item size={4}>
                    <RangeSelectArea
                        comparableType={props.comparableType}
                        label="Building Area"
                        attrName="buildingArea"
                        sx={{
                            width: '100%',
                        }}
                    />
                </Grid>
                <Grid item size={4}>
                    <RangeSelectArea
                        comparableType={props.comparableType}
                        label="Equivalent Building Area"
                        attrName="equivalentBuildingArea"
                        sx={{
                            width: '100%',
                        }}
                    />
                </Grid>
                <Grid item size={4}>
                    <RangeAutocomplete
                        comparableType={props.comparableType}
                        attrName="floorLevelOfUnit"
                        label="Floor Level of Unit"
                        subLabel="Select an option or manually enter a value"
                        minOptions={ONE_TO_SIX_OPTIONS_MIN}
                        maxOptions={ONE_TO_SIX_OPTIONS_MAX}
                        isDropdown={false}
                        validateInput={validateInput}
                    />
                </Grid>
                <Grid item size={4}>
                    <RangeAutocomplete
                        comparableType={props.comparableType}
                        attrName="floorLevelsInsideUnit"
                        label="No. Floor Levels Inside Unit"
                        subLabel="Select an option or manually enter a value"
                        minOptions={ONE_TO_SIX_OPTIONS_MIN}
                        maxOptions={ONE_TO_SIX_OPTIONS_MAX}
                        isDropdown={false}
                        validateInput={validateInput}
                    />
                </Grid>
                <Grid item size={4}>
                    <RangeAutocomplete
                        comparableType={props.comparableType}
                        attrName="flats"
                        label="Number of Flats"
                        subLabel="Select an option or manually enter a value"
                        minOptions={ONE_TO_SIX_OPTIONS_MIN}
                        maxOptions={ONE_TO_SIX_OPTIONS_MAX}
                        isDropdown={false}
                        validateInput={validateInput}
                    />
                </Grid>
                <Grid item size={4}>
                    <RangeAutocomplete
                        comparableType={props.comparableType}
                        attrName="streetFrontage"
                        label="Street Frontage (m)"
                        subLabel="Select an option or manually enter a value"
                        minOptions={STREET_FRONTAGE_OPTIONS_MIN}
                        maxOptions={STREET_FRONTAGE_OPTIONS_MAX}
                        isDropdown={false}
                        formatter={SearchFilterUtil.formatStreetFrontage}
                        validateInput={validateDistanceInput}
                    />
                </Grid>
                <Grid item size={4}>
                    <RangeAutocomplete
                        comparableType={props.comparableType}
                        attrName="totalFloorsInBuilding"
                        label="Total Floors in Building"
                        subLabel="Select an option or manually enter a value"
                        minOptions={ONE_TO_SIX_OPTIONS_MIN}
                        maxOptions={ONE_TO_SIX_OPTIONS_MAX}
                        isDropdown={false}
                        validateInput={validateInput}
                    />
                </Grid>
                <Grid item size={4}>
                    <YearBuiltFilter
                        comparableType={props.comparableType}
                        attrName="yearBuildingRefurbished"
                        label="Year Building Refurbished"
                    />
                </Grid>
                <Grid item size={4}>
                    <YearBuiltFilter
                        comparableType={props.comparableType}
                        attrName="yearEstablished"
                        label="Year Established"
                    />
                </Grid>
                <Grid item size={4}>
                    <CheckboxFilter
                        value="Free Standing Building"
                        comparableType={props.comparableType}
                        attrName="freeStandingBuilding"
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

StructureFilter.propTypes = {
    comparableType: PropTypes.string.isRequired,
};

export default React.memo(StructureFilter);